import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getBlockedCompanies, addBlockedCompany,
  removeBlockedCompany, updateBlockedCompany, getEmployerNames,
} from '../api/services';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { formatDate } from '../utils/formatDate';
import { DEFAULT_PAGE_SIZE } from '../components/Pagination';

const DEFAULT_FORM = { name: '', reason: 'Fraud / Suspicious activity', notes: '' };
const REASONS = ['Fraud / Suspicious activity', 'Bad cheques', 'Account closed', 'Blacklisted', 'Other'];

/* ── Inline styles ─────────────────────────────────────────────────────────── */
const S = {
  /* Overlay backdrop */
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(15,25,35,0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  /* Modal card */
  modal: {
    background: '#fff',
    borderRadius: 20,
    width: 500,
    maxWidth: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
    display: 'flex',
    flexDirection: 'column',
  },
  /* Red top strip */
  modalHeader: {
    background: 'linear-gradient(135deg,#b91c1c 0%,#e03c3c 100%)',
    borderRadius: '20px 20px 0 0',
    padding: '22px 28px 18px',
    color: '#fff',
  },
  modalTitle: { fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', margin: 0 },
  modalSub:   { fontSize: 12.5, opacity: 0.85, marginTop: 4 },
  modalBody:  { padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 },
  modalFoot:  {
    padding: '16px 28px 24px',
    display: 'flex', gap: 10, justifyContent: 'flex-end',
    borderTop: '1px solid #f1f5f9',
  },

  /* Section label */
  label: {
    display: 'block', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: '#64748b', marginBottom: 8,
  },

  /* Employer pills */
  pillWrap: {
    display: 'flex', flexWrap: 'wrap', gap: 7,
    maxHeight: 110, overflowY: 'auto',
    padding: '10px 12px',
    background: '#f8fafc',
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
  },
  pill: (selected, blocked) => ({
    padding: '5px 13px',
    borderRadius: 30,
    fontSize: 12.5,
    fontWeight: selected ? 700 : 500,
    border: selected
      ? '2px solid #b91c1c'
      : blocked ? '1.5px solid #fca5a5' : '1.5px solid #cbd5e1',
    background: selected ? '#fef2f2' : blocked ? '#fff5f5' : '#fff',
    color: selected ? '#b91c1c' : blocked ? '#dc2626' : '#374151',
    cursor: blocked ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 5,
    opacity: blocked ? 0.65 : 1,
  }),

  /* Text input */
  input: (focused) => ({
    width: '100%',
    padding: '11px 14px',
    border: focused ? '2px solid #b91c1c' : '1.5px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.15s',
    background: '#fff',
  }),

  /* Dropdown */
  dropdown: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
    maxHeight: 220, overflowY: 'auto',
    zIndex: 10,
  },
  dropItem: (hovered, blocked) => ({
    padding: '10px 14px',
    cursor: blocked ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', gap: 10,
    background: hovered && !blocked ? '#fef2f2' : blocked ? '#fff5f5' : '#fff',
    borderBottom: '1px solid #f8fafc',
    transition: 'background 0.12s',
  }),

  /* Reason chips */
  chip: (active) => ({
    padding: '6px 14px',
    borderRadius: 30,
    fontSize: 12.5,
    fontWeight: active ? 700 : 500,
    border: active ? '2px solid #b91c1c' : '1.5px solid #e2e8f0',
    background: active ? '#fef2f2' : '#fff',
    color: active ? '#b91c1c' : '#475569',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  }),

  /* Buttons */
  btnCancel: {
    padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
    border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  btnSave: (disabled) => ({
    padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700,
    border: 'none',
    background: disabled ? '#fca5a5' : 'linear-gradient(135deg,#b91c1c,#e03c3c)',
    color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'flex', alignItems: 'center', gap: 7,
  }),
};

/* ── Autocomplete field ─────────────────────────────────────────────────────── */
function CompanyField({ value, onChange, employers, blockedSet }) {
  const [focused, setFocused]     = useState(false);
  const [hovered, setHovered]     = useState(null);
  const wrapRef                   = useRef(null);

  const filtered = employers.filter(e =>
    e.toLowerCase().includes(value.toLowerCase()) && e !== ''
  );
  const showDrop = focused && (filtered.length > 0 || (value.trim() && !employers.some(e => e.toLowerCase() === value.toLowerCase())));

  useEffect(() => {
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        style={S.input(focused)}
        value={value}
        placeholder="Type or select a company name…"
        autoComplete="off"
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
      />
      {showDrop && (
        <div style={S.dropdown}>
          {filtered.map((name, i) => {
            const blocked = blockedSet.has(name.toLowerCase().trim());
            return (
              <div
                key={name}
                style={S.dropItem(hovered === i, blocked)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onMouseDown={e => {
                  e.preventDefault();
                  if (!blocked) { onChange(name); setFocused(false); }
                }}
              >
                <span style={{ fontSize: 16 }}>{blocked ? '🚫' : '🏢'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: blocked ? '#dc2626' : '#1e293b' }}>{name}</div>
                  {blocked && <div style={{ fontSize: 11, color: '#dc2626' }}>Already blocked</div>}
                </div>
                {!blocked && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>↵ Select</span>}
              </div>
            );
          })}
          {/* Manual entry option when not in list */}
          {value.trim() && !employers.some(e => e.toLowerCase() === value.toLowerCase()) && (
            <div
              style={S.dropItem(hovered === 'manual', false)}
              onMouseEnter={() => setHovered('manual')}
              onMouseLeave={() => setHovered(null)}
              onMouseDown={e => { e.preventDefault(); onChange(value.trim()); setFocused(false); }}
            >
              <span style={{ fontSize: 16 }}>✏️</span>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b' }}>Use: "{value.trim()}"</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Not in customer records — add manually</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * BlockedCompanies Page Component.
 * Lists all companies flagged for fraud/suspicious activity and provides blocking/unblocking controls.
 *
 * @component
 * @returns {JSX.Element} BlockedCompanies page view.
 */
export default function BlockedCompanies() {
  const [companies, setCompanies] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(DEFAULT_FORM);
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null);
  const [page, setPage]           = useState(1);
  const [limit]                   = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  void totalPages;

  const blockedSet = new Set(companies.map(c => c.name.toLowerCase().trim()));
  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const fetchAll = useCallback(async (q = search, pg = page, lmt = limit) => {
    setLoading(true);
    try {
      const [compRes, emplRes] = await Promise.all([
        getBlockedCompanies(q ? { search: q, page: pg, limit: lmt } : { page: pg, limit: lmt }),
        getEmployerNames(),
      ]);
      setCompanies(compRes.data.data || []);
      setTotal(compRes.data.total || 0);
      setTotalPages(compRes.data.totalPages || 1);
      setEmployers(emplRes.data.data || []);
    } catch { toast.error('Failed to load data.'); }
    finally   { setLoading(false); }
  }, [search, page, limit]); // eslint-disable-line

  const lastFetchTimeRef   = useRef(0);
  const lastFetchParamsRef = useRef("");

  useEffect(() => {
    const paramsKey = JSON.stringify({ page, limit });
    const now = Date.now();
    if (paramsKey === lastFetchParamsRef.current && now - lastFetchTimeRef.current < 400) return;
    lastFetchParamsRef.current = paramsKey;
    lastFetchTimeRef.current = now;
    fetchAll(search, page, limit);
  }, [page, limit]); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchAll(search, 1, limit);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line

  const openAdd  = () => { setEditId(null); setForm(DEFAULT_FORM); setShowModal(true); };
  void openAdd;
  const openEdit = (c) => { setEditId(c._id); setForm({ name: c.name, reason: c.reason || '', notes: c.notes || '' }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditId(null); setForm(DEFAULT_FORM); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      editId ? await updateBlockedCompany(editId, form) : await addBlockedCompany(form);
      toast.success(editId ? 'Company updated.' : `"${form.name}" blocked.`);
      closeModal();
      fetchAll('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleUnblock = async (id, name) => {
    const r = await Swal.fire({
      title: 'Unblock Company?',
      html: `<b>${name}</b> will be allowed for transactions again.`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: '✅ Yes, Unblock', cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a', cancelButtonColor: '#6b7280',
    });
    if (!r.isConfirmed) return;
    try { await removeBlockedCompany(id); toast.success(`"${name}" unblocked.`); fetchAll(''); }
    catch { toast.error('Failed to unblock.'); }
  };

  return (
    <Layout title="Blocked Companies">

      {/* ── Page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f1923', display: 'flex', alignItems: 'center', gap: 8 }}>
            🚫 Blocked Companies
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            {loading ? 'Loading…' : `${total} blocked compan${total !== 1 ? 'ies' : 'y'} · To block/unblock, use the Companies page`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => fetchAll('')} className="btn btn-outline btn-sm">🔄 Refresh</button>
          <a href="/companies" className="btn btn-outline btn-sm"
            style={{ fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>
            🏢 Manage in Companies
          </a>
        </div>
      </div>

      {/* Warning strip */}
      {!loading && companies.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>
            Cheques from these companies will be automatically rejected during cashing.
          </span>
        </div>
      )}

      {/* Search bar */}
      <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            className="form-control"
            placeholder="Search by company name or reason…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchAll(search)}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary btn-sm" onClick={() => fetchAll(search)}>
            🔍 Search
          </button>
          {search && (
            <button className="btn btn-outline btn-sm" onClick={() => { setSearch(''); fetchAll(''); }}>✕ Clear</button>
          )}
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div style={S.backdrop} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div style={S.modal}>

            {/* Header */}
            <div style={S.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 28 }}>{editId ? '✏️' : '🚫'}</span>
                <div>
                  <div style={S.modalTitle}>{editId ? 'Edit Blocked Company' : 'Block a Company'}</div>
                  <div style={S.modalSub}>
                    {editId ? 'Update the details for this blocked company.' : 'Select from customer employer records or enter manually.'}
                  </div>
                </div>
              </div>
            </div>

            <div style={S.modalBody}>

              {/* ── Company Name ── */}
              <div>
                <label style={S.label}>Company / Maker Name *</label>

                {/* Employer pills — only when adding */}
                {!editId && employers.length > 0 && (
                  <>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 8, fontWeight: 500 }}>
                      📋 From customer employer records — click to fill:
                    </div>
                    <div style={S.pillWrap}>
                      {employers.map(name => {
                        const blocked  = blockedSet.has(name.toLowerCase().trim());
                        const selected = form.name === name;
                        return (
                          <button
                            key={name}
                            type="button"
                            title={blocked ? 'Already blocked' : `Click to select "${name}"`}
                            style={S.pill(selected, blocked)}
                            onClick={() => { if (!blocked) setF('name', name); }}
                          >
                            <span>{blocked ? '🚫' : selected ? '✅' : '🏢'}</span>
                            {name}
                            {blocked && <span style={{ fontSize: 10, background: '#fca5a5', color: '#7f1d1d', borderRadius: 4, padding: '1px 5px' }}>blocked</span>}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', margin: '10px 0 6px', textAlign: 'center' }}>
                      — or type a name below —
                    </div>
                  </>
                )}

                <CompanyField
                  value={form.name}
                  onChange={val => setF('name', val)}
                  employers={employers}
                  blockedSet={blockedSet}
                />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  Must match the "Maker" field on the cheque (case-insensitive).
                </div>

                {/* Selected company preview */}
                {form.name.trim() && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 22 }}>🚫</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>{form.name}</div>
                      <div style={{ fontSize: 11, color: '#dc2626' }}>Will be blocked for all transactions</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setF('name', '')}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, lineHeight: 1 }}
                    >×</button>
                  </div>
                )}
              </div>

              {/* ── Reason ── */}
              <div>
                <label style={S.label}>Reason</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
                  {REASONS.map(r => (
                    <button key={r} type="button" style={S.chip(form.reason === r)} onClick={() => setF('reason', r)}>{r}</button>
                  ))}
                </div>
                <input
                  style={S.input(false)}
                  placeholder="Or type a custom reason…"
                  value={form.reason}
                  onChange={e => setF('reason', e.target.value)}
                />
              </div>

              {/* ── Notes ── */}
              <div>
                <label style={S.label}>Notes (optional)</label>
                <textarea
                  rows={3}
                  style={{ ...S.input(false), resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Internal notes about why this company is being blocked…"
                  value={form.notes}
                  onChange={e => setF('notes', e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={S.modalFoot}>
              <button style={S.btnCancel} onClick={closeModal}>Cancel</button>
              <button
                style={S.btnSave(saving || !form.name.trim())}
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
              >
                {saving
                  ? <><span>⏳</span> Saving…</>
                  : editId
                    ? <><span>💾</span> Update</>
                    : <><span>🚫</span> Block Company</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Company / Maker Name</th>
                <th>Reason</th>
                <th>Notes</th>
                <th>Blocked On</th>
                <th>Blocked By</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 56 }}>
                  <div className="spinner spinner-dark" style={{ margin: '0 auto' }} />
                </td></tr>
              )}
              {!loading && companies.length === 0 && (
                <tr><td colSpan={7}>
                  <div className="empty-state">
                    <div className="empty-icon">✅</div>
                    <h3>No blocked companies</h3>
                    <p>Click "🚫 Block Company" to flag one.</p>
                  </div>
                </td></tr>
              )}
              {!loading && companies.map((c, idx) => (
                <tr key={c._id}>
                  <td style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{idx + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 9, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🚫</div>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {c.reason || 'Fraud / Suspicious activity'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12.5, color: '#64748b', maxWidth: 180 }}>{c.notes || <span style={{ opacity: 0.35 }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>
                    {c.blockedAt ? formatDate(c.blockedAt) : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{c.blockedBy?.name || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(c)}>✏️</button>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #6ee7b7', fontWeight: 700 }}
                        onClick={() => handleUnblock(c._id, c.name)}
                      >✅ Unblock</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
